import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Loader from '../../components/Loader';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { Settings } from '../../client/services';

export const PRODUCT_BADGE_SETTING_NAME = 'product_badge_config';

type NumberFieldKey = Exclude<keyof ProductBadgeFormValues, 'activeState'>;

export type ProductBadgeFormValues = {
  newDays: number;
  bestSellerWindowDays: number;
  bestSellerTopK: number;
  trendingRecentDays: number;
  trendingPreviousDays: number;
  trendingGrowthPct: number;
  trendingMinRecentCount: number;
  trendingTopT: number;
  activeState: boolean;
};

export const DEFAULT_PRODUCT_BADGE_FORM: ProductBadgeFormValues = {
  newDays: 30,
  bestSellerWindowDays: 90,
  bestSellerTopK: 10,
  trendingRecentDays: 7,
  trendingPreviousDays: 7,
  trendingGrowthPct: 50,
  trendingMinRecentCount: 5,
  trendingTopT: 8,
  activeState: true,
};

const NEGATIVE_ERROR = 'Negative values are not allowed';

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalize = (value: unknown, fallback: number) => {
  const num = toNumber(value, fallback);
  return num < 0 ? 0 : Math.floor(num);
};

export const parseProductBadgeValue = (
  raw?: string | null
): ProductBadgeFormValues => {
  if (!raw) return { ...DEFAULT_PRODUCT_BADGE_FORM };
  try {
    const parsed = JSON.parse(raw);
    return {
      newDays:
        normalize(parsed?.newDays, DEFAULT_PRODUCT_BADGE_FORM.newDays) ||
        DEFAULT_PRODUCT_BADGE_FORM.newDays,
      bestSellerWindowDays:
        normalize(
          parsed?.bestSellerWindowDays,
          DEFAULT_PRODUCT_BADGE_FORM.bestSellerWindowDays
        ) || DEFAULT_PRODUCT_BADGE_FORM.bestSellerWindowDays,
      bestSellerTopK:
        normalize(
          parsed?.bestSellerTopK,
          DEFAULT_PRODUCT_BADGE_FORM.bestSellerTopK
        ) || DEFAULT_PRODUCT_BADGE_FORM.bestSellerTopK,
      trendingRecentDays:
        normalize(
          parsed?.trendingRecentDays,
          DEFAULT_PRODUCT_BADGE_FORM.trendingRecentDays
        ) || DEFAULT_PRODUCT_BADGE_FORM.trendingRecentDays,
      trendingPreviousDays:
        normalize(
          parsed?.trendingPreviousDays,
          DEFAULT_PRODUCT_BADGE_FORM.trendingPreviousDays
        ) || DEFAULT_PRODUCT_BADGE_FORM.trendingPreviousDays,
      trendingGrowthPct: normalize(
        parsed?.trendingGrowthPct,
        DEFAULT_PRODUCT_BADGE_FORM.trendingGrowthPct
      ),
      trendingMinRecentCount:
        normalize(
          parsed?.trendingMinRecentCount,
          DEFAULT_PRODUCT_BADGE_FORM.trendingMinRecentCount
        ) || DEFAULT_PRODUCT_BADGE_FORM.trendingMinRecentCount,
      trendingTopT:
        normalize(
          parsed?.trendingTopT,
          DEFAULT_PRODUCT_BADGE_FORM.trendingTopT
        ) || DEFAULT_PRODUCT_BADGE_FORM.trendingTopT,
      activeState: parsed?.activeState !== false,
    };
  } catch {
    return { ...DEFAULT_PRODUCT_BADGE_FORM };
  }
};

const ProductBadgeSettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [values, setValues] = useState<ProductBadgeFormValues>({
    ...DEFAULT_PRODUCT_BADGE_FORM,
  });
  const [savedValues, setSavedValues] = useState<ProductBadgeFormValues>({
    ...DEFAULT_PRODUCT_BADGE_FORM,
  });
  const [errors, setErrors] = useState<Partial<Record<NumberFieldKey, string>>>(
    {}
  );

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await Settings.find({
        query: {
          search: PRODUCT_BADGE_SETTING_NAME,
          limit: 10,
          skip: 0,
        },
      });
      const setting =
        (response?.data || []).find(
          (item: any) => item.name === PRODUCT_BADGE_SETTING_NAME
        ) || null;

      if (setting) {
        const parsed = parseProductBadgeValue(setting.value);
        setSettingId(setting.id);
        setValues(parsed);
        setSavedValues(parsed);
        setErrors({});
      } else {
        setSettingId(null);
        setValues({ ...DEFAULT_PRODUCT_BADGE_FORM });
        setSavedValues({ ...DEFAULT_PRODUCT_BADGE_FORM });
        setErrors({});
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load product badge settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setFieldError = (field: NumberFieldKey, message?: string) => {
    setErrors((prev) => {
      if (!message) {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: message };
    });
  };

  const handleNumberChange =
    (field: NumberFieldKey) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = toNumber(event.target.value, 0);

      if (Number.isFinite(parsed) && parsed < 0) {
        setValues((prev) => ({ ...prev, [field]: 0 }));
        setFieldError(field, NEGATIVE_ERROR);
        return;
      }

      setValues((prev) => ({
        ...prev,
        [field]: Math.max(0, Math.floor(parsed)),
      }));
      setFieldError(field, undefined);
    };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({
      ...prev,
      activeState: event.target.checked,
    }));
  };

  const handleCancel = () => {
    setValues(savedValues);
    setErrors({});
  };

  const validateBeforeSave = () => {
    const nextErrors: Partial<Record<NumberFieldKey, string>> = {};
    (
      [
        'newDays',
        'bestSellerWindowDays',
        'bestSellerTopK',
        'trendingRecentDays',
        'trendingPreviousDays',
        'trendingGrowthPct',
        'trendingMinRecentCount',
        'trendingTopT',
      ] as NumberFieldKey[]
    ).forEach((field) => {
      if (Number(values[field]) < 0) {
        nextErrors[field] = NEGATIVE_ERROR;
      }
    });

    if (!values.newDays) {
      nextErrors.newDays = 'Must be at least 1 day';
    }
    if (!values.bestSellerWindowDays) {
      nextErrors.bestSellerWindowDays = 'Must be at least 1 day';
    }
    if (!values.bestSellerTopK) {
      nextErrors.bestSellerTopK = 'Must be at least 1';
    }
    if (!values.trendingRecentDays) {
      nextErrors.trendingRecentDays = 'Must be at least 1 day';
    }
    if (!values.trendingPreviousDays) {
      nextErrors.trendingPreviousDays = 'Must be at least 1 day';
    }
    if (!values.trendingMinRecentCount) {
      nextErrors.trendingMinRecentCount = 'Must be at least 1';
    }
    if (!values.trendingTopT) {
      nextErrors.trendingTopT = 'Must be at least 1';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) {
      toast.error('Please fix the highlighted fields');
      return;
    }

    setIsSaving(true);
    try {
      const sanitizedValues: ProductBadgeFormValues = {
        newDays: Math.max(1, values.newDays),
        bestSellerWindowDays: Math.max(1, values.bestSellerWindowDays),
        bestSellerTopK: Math.max(1, values.bestSellerTopK),
        trendingRecentDays: Math.max(1, values.trendingRecentDays),
        trendingPreviousDays: Math.max(1, values.trendingPreviousDays),
        trendingGrowthPct: Math.max(0, values.trendingGrowthPct),
        trendingMinRecentCount: Math.max(1, values.trendingMinRecentCount),
        trendingTopT: Math.max(1, values.trendingTopT),
        activeState: values.activeState,
      };

      const payload = {
        name: PRODUCT_BADGE_SETTING_NAME,
        value: JSON.stringify(sanitizedValues),
        valueType: 'json',
        language: '',
        activeState: true,
      };

      if (settingId) {
        await Settings.patch(settingId, payload);
      } else {
        const created = await Settings.create(payload);
        setSettingId(created?.id || null);
      }

      setValues(sanitizedValues);
      setSavedValues(sanitizedValues);
      setErrors({});
      toast.success('Product badge settings saved');
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save product badge settings');
    } finally {
      setIsSaving(false);
    }
  };

  const numberFieldProps = (field: NumberFieldKey, helperText?: string) => ({
    fullWidth: true as const,
    value: values[field],
    onChange: handleNumberChange(field),
    error: Boolean(errors[field]),
    helperText: errors[field] || helperText,
    InputProps: {
      inputComponent: NumericFormatCustom as any,
    },
    inputProps: {
      allowNegative: false,
      min: 0,
    },
  });

  return (
    <Page title='Product Badges'>
      <Container
        maxWidth='xl'
        sx={{ minWidth: 0, px: { xs: 1.5, sm: 2, md: 3 } }}
      >
        <HeaderBreadcrumbs
          heading='Product Badges'
          links={[{ name: 'Dashboard', href: '/' }]}
        />

        <Card sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
          {isLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <Loader />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Typography variant='body2' color='text.secondary'>
                The app shows only one tag per
                item, in this order: Trending, then Best Seller, then New.
                Changes apply after the daily badge job.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.activeState}
                    onChange={handleActiveChange}
                    color='primary'
                  />
                }
                label='Badge job active'
              />

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  New
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Show New for this many days'
                      {...numberFieldProps(
                        'newDays',
                        'Item stays New from the day it is created until this many days pass'
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Best Seller
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Look at orders from last (days)'
                      {...numberFieldProps(
                        'bestSellerWindowDays',
                        'Count sales in this window (cancelled and temp orders are ignored)'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='How many items get Best Seller'
                      {...numberFieldProps(
                        'bestSellerTopK',
                        'Top sellers of each type (shape, topping, cake, bundle)'
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Trending
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Recent days'
                      {...numberFieldProps(
                        'trendingRecentDays',
                        'Sales in this recent window'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Compare with previous days'
                      {...numberFieldProps(
                        'trendingPreviousDays',
                        'Compared against the same number of days before that'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Minimum growth %'
                      {...numberFieldProps(
                        'trendingGrowthPct',
                        'Sales must grow at least this much vs the previous window'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Minimum recent orders'
                      {...numberFieldProps(
                        'trendingMinRecentCount',
                        'Need at least this many recent orders so noise does not trend'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='How many items get Trending'
                      {...numberFieldProps(
                        'trendingTopT',
                        'Highest-growth items of each type, up to this number'
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Stack direction='row' spacing={2} justifyContent='flex-end'>
                <LoadingButton
                  variant='outlined'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </LoadingButton>
                <LoadingButton
                  variant='contained'
                  loading={isSaving}
                  onClick={handleSave}
                >
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default ProductBadgeSettingsPage;
